<template>
    <div>
        <a v-if="!isPublicRoute && !isAwaitingLogin" href="#main-content" class="skip-to-main">
            Skip to main content
        </a>

        <SideNavBar v-if="isMobile && !isPublicRoute && !loggingIn && !isAwaitingLogin" />

        <CommandPalette v-if="!isPublicRoute && !isAwaitingLogin" />
        <ScanLabelModal v-if="!isPublicRoute && !isAwaitingLogin" />

        <Loading
            loader="bars"
            :active="!isPublicRoute && initialLoginLoading"
            :is-full-page="true"
            :opacity="1"
        />

        <!-- <div
            style="
                position: fixed;
                z-index: 10000;
                right: 10px;
                top: 10px;
                padding: 20px;
                background-color: rgba(0, 0, 0, 0.7);
                color: white;
                font-size: 12px;
                border-radius: 8px;
            "
        >
            <strong style="text-align: center; display: block">App State Variables:</strong>
            <table>
                <tr
                    :style="{
                        backgroundColor: isMobile ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">isMobile:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ isMobile }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: loggingIn ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">loggingIn:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ loggingIn }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: initialLoginLoading ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">initialLoginLoading:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ initialLoginLoading }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: isAwaitingLogin ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">isAwaitingLogin:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ isAwaitingLogin }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: userDataLoaded ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">userDataLoaded:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ userDataLoaded }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: userIsAdminOverride ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">userIsAdminOverride:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ userIsAdminOverride }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: isRouteLoading ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">isRouteLoading:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ isRouteLoading }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: isTrialExpired ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">isTrialExpired:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ isTrialExpired }}
                    </td>
                </tr>
                <tr
                    :style="{
                        backgroundColor: devMode() ? '#81c994' : '#c98181'
                    }"
                >
                    <td style="padding: 4px; text-align: right">devMode:</td>
                    <td style="padding: 4px; text-align: left">
                        {{ devMode() }}
                    </td>
                </tr>
            </table>
        </div> -->

        <!-- EXPERIMENT (framed app shell): global top bar. Spans the full width
             above both nav and content, the way Klaviyo's does. It surfaces the
             command palette, which is otherwise keyboard-only on desktop. -->
        <header v-if="framedShell" class="app-topbar">
            <div class="app-topbar__lead">
                <router-link to="/" class="app-topbar__logo" aria-label="Dashboard">
                    <img :src="textLogoBlack" alt="Forkto" />
                </router-link>

                <!-- Location and department describe the operating scope, so
                     they stay grouped behind one quiet pill. The active user
                     is a working identity and remains visible beside it. -->
                <ContextMenu
                    v-if="multipleLocations || multipleDepartments"
                    :show-location="multipleLocations"
                    :show-department="multipleDepartments"
                />
                <UserSwitcher
                    v-if="hasFeatureFlag(Flags.EasyUserSwitch)"
                    class="app-topbar__user-switcher"
                    topbar
                />
            </div>

            <div class="app-topbar__command-cluster">
                <!-- Global search finds pages, actions, records, and help. The
                     button opens the palette and keeps the keyboard shortcut visible. -->
                <button
                    type="button"
                    class="app-topbar__command"
                    data-cy="app-topbar-command-search"
                    aria-label="Search Forkto"
                    aria-haspopup="dialog"
                    :aria-expanded="$store.state.commandPaletteOpen"
                    aria-keyshortcuts="Control+k Meta+k"
                    @click="openCommandPalette"
                >
                    <i class="fas fa-search" aria-hidden="true"></i>
                    <span class="app-topbar__command-label">Search Forkto</span>
                    <kbd class="app-topbar__kbd">{{ searchShortcutHint }}</kbd>
                </button>

                <ChatNotifications />
                <PersistentAnnouncements v-if="!isDashboardRoute" mode="compact" />
            </div>

            <div class="app-topbar__context">
                <!-- Scanning a label is one of the product's primary verbs, so it
                     is reachable from every page rather than only from the
                     Production nav. Gated like IntakesView's own scan action. -->
                <button
                    v-if="canScanLabel"
                    type="button"
                    class="app-topbar__scan"
                    data-cy="topbar-scan"
                    @click="openScanLabel"
                >
                    <i class="fas fa-qrcode" aria-hidden="true"></i>
                    Trace
                </button>

                <!-- Context help is an action, so it lives with the other actions
                     up here rather than in the nav. The article follows the route. -->
                <a
                    :href="contextHelp.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="app-topbar__help"
                    :title="contextHelp.specific ? `Help: ${contextHelp.title}` : 'Help centre'"
                    :aria-label="
                        contextHelp.specific ? `Help for ${contextHelp.title}` : 'Help centre'
                    "
                    data-cy="topbar-help"
                >
                    <i class="fas fa-question-circle" aria-hidden="true"></i>
                </a>

                <ActionMenu
                    title="Account"
                    label="Account menu"
                    trigger-class="app-topbar__account"
                    menu-class="account-menu"
                    :trigger-attrs="{ 'data-cy': 'topbar-account' }"
                >
                    <template #trigger
                        ><span class="app-topbar__avatar" aria-hidden="true">
                            <template v-if="accountInitials">{{ accountInitials }}</template>
                            <i v-else class="fas fa-user"></i>
                        </span>
                        <i class="fas fa-chevron-down app-topbar__caret" aria-hidden="true"></i>
                    </template>
                    <li v-if="accountName || accountRoleLabel" class="account-menu__identity">
                        <span class="app-topbar__avatar" aria-hidden="true">
                            <template v-if="accountInitials">{{ accountInitials }}</template>
                            <i v-else class="fas fa-user"></i>
                        </span>
                        <span class="account-menu__identity-text">
                            <span class="account-menu__name-row">
                                <strong v-if="accountName" class="account-menu__name">
                                    {{ accountName }}
                                </strong>
                                <span v-if="accountRoleLabel" class="account-menu__role">
                                    {{ accountRoleLabel }}
                                </span>
                            </span>
                            <span v-if="accountEmail" class="account-menu__email">
                                {{ accountEmail }}
                            </span>
                        </span>
                    </li>
                    <li>
                        <router-link class="dropdown-item account-menu__item" to="/profile">
                            Profile
                        </router-link>
                    </li>
                    <li>
                        <router-link class="dropdown-item account-menu__item" to="/settings">
                            Settings
                        </router-link>
                    </li>
                    <li>
                        <button
                            type="button"
                            class="dropdown-item account-menu__item"
                            @click="openSupport"
                        >
                            Support
                        </button>
                    </li>
                    <li><hr class="account-menu__divider" /></li>
                    <li>
                        <button
                            type="button"
                            class="dropdown-item account-menu__item account-menu__item--danger"
                            data-cy="topbar-logout"
                            @click="requestLogout"
                        >
                            Sign Out
                        </button>
                    </li>
                </ActionMenu>
            </div>
        </header>

        <div class="d-flex flex-nowrap" :class="{ 'app-framed': framedShell }">
            <!-- Sidebar -->
            <div
                v-if="!isPublicRoute && !isAwaitingLogin && !isMobile"
                id="sidebarColumn"
                class="flex-shrink-0"
                :style="sidebarColumnStyle"
            >
                <SideNavContent />

                <!-- Foot of the nav: the collapse toggle for the nav it controls. -->
                <div v-if="framedShell" class="sidebar-foot">
                    <button
                        v-if="framedShell"
                        type="button"
                        class="sidebar-collapse-toggle"
                        :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                        :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                        :aria-pressed="sidebarCollapsed"
                        @click="toggleSidebarCollapsed"
                    >
                        <i
                            :class="[
                                'fas',
                                sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'
                            ]"
                            aria-hidden="true"
                        ></i>
                        <span v-if="!sidebarCollapsed" class="sidebar-collapse-toggle__label">
                            Collapse
                        </span>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="app-main-scroll flex-grow-1" style="height: 100vh; height: 100svh">
                <div
                    v-if="!isPublicRoute && !isAwaitingLogin && showRouteProgress"
                    class="route-progress"
                    role="progressbar"
                    aria-label="Loading page"
                >
                    <span class="route-progress__bar"></span>
                </div>

                <div class="app-main-inner">
                    <TrialExpiredAlert v-if="!isPublicRoute && isTrialExpired" />
                    <main
                        v-if="isPublicRoute || (!isAwaitingLogin && !initialLoginLoading)"
                        id="main-content"
                        class="disable-ios-overscroll"
                        :class="{
                            'app-main--chat': $route.name === 'Chat',
                            'native-safe-area-scrim':
                                $store.state.isMobileOS && !isPublicRoute && !isAwaitingLogin
                        }"
                        :style="isMobile ? mobileStyles : {}"
                        tabindex="-1"
                    >
                        <PersistentAnnouncements
                            v-if="!isPublicRoute && (isDashboardRoute || isMobile)"
                            :mode="isDashboardRoute ? 'banner' : 'compact'"
                        />
                        <RouterView />
                    </main>

                    <MobileBottomBar
                        v-if="!isPublicRoute && !isAwaitingLogin && isMobile"
                        :chat-unread="chatUnreadCount"
                    />

                    <!-- Legal Modal -->
                    <ForkModal
                        v-if="!isAwaitingLogin"
                        id="legal-modal"
                        ref="legal-modal"
                        modal-title="Legal"
                        :hide-header="true"
                        :body-safe-bottom="true"
                        size="modal-md"
                        dialog-class="modal-dialog-scrollable"
                    >
                        <div class="modal-body">
                            <Legal
                                :required-documents="requiredLegalDocuments"
                                @legal-accepted="closeLegalModal"
                            />
                        </div>
                    </ForkModal>

                    <!-- Biometric Enrollment Modal -->
                    <BiometricEnrollModal
                        v-if="pendingBiometricEnroll"
                        ref="biometric-enroll-modal"
                        :biometry-type="biometryType"
                        @enrolled="handleBiometricEnrolled"
                        @dismissed="handleBiometricDismissed"
                    />
                </div>
            </div>
        </div>

        <DevPanel v-if="!isPublicRoute && canAccessDevMode" />
    </div>
</template>
<script>
import { openSupport } from "@/services/openSupport";
import ActionMenu from "@/components/common/ActionMenu.vue";
import { computed } from "vue";
import { Capacitor, SystemBars, SystemBarsStyle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { App as CapacitorApp } from "@capacitor/app";

import { Tooltip } from "bootstrap";
import { mapGetters } from "vuex";
import { setAPICreds, setLoggingIn } from "@/classes/DataAPI.js";
import { API } from "@/services/apiInstance";
import { featureFlags } from "@/services/featureFlagsInstance";
import { userIsRole } from "@/services/AuthService";
import { checkForAppUpdate } from "@/services/appUpdateRecovery";
import { thirdPartyScriptsEnabled } from "@/services/thirdPartyScripts";
import { tawkSession } from "@/services/tawkSession";
import { markAppReady } from "@/store";
import { normalizeCustomerID } from "@/classes/customerID";
import { getKioskUsers, removeKioskUsers, setKioskUsers } from "@/classes/kioskUsers";

// Loading Overlay
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

import Legal from "./components/Legal.vue";
import ForkModal from "./components/common/ForkModal.vue"; // Import this as it sometimes fails to resolve the component
import SideNavBar from "@/components/SideNavBar.vue";
import SideNavContent from "@/components/common/SideNavContent.vue";
import textLogoBlack from "@/assets/TextLogoBlack.svg";
import ContextMenu from "@/components/common/ContextMenu.vue";
import UserSwitcher from "@/components/UserSwitcher.vue";
import ScanLabelModal from "@/components/common/ScanLabelModal.vue";
import { SCAN_LABEL_OPEN } from "@/composables/useUniversalRecordScan";
import { resolveHelpForRoute } from "@/composables/useContextHelp";
import TrialExpiredAlert from "@/components/TrialExpiredAlert.vue";
import DevPanel from "@/components/DevPanel.vue";
import BiometricEnrollModal from "@/components/common/BiometricEnrollModal.vue";
import CommandPalette from "@/components/CommandPalette.vue";
import MobileBottomBar from "@/components/common/MobileBottomBar.vue";
import { useChatSummary, chatAccessEnabled } from "@/composables/queries/useChat";
import { installNotificationPush } from "@/services/notificationPush";
import {
    startNotificationDevices,
    syncNotificationDevice,
    stopNotificationDevices
} from "@/services/notificationDevices";
import ChatNotifications from "@/components/chat/ChatNotifications.vue";
import { featureFlagsLoaded } from "@/classes/FeatureFlags";
import { chatAppActive } from "@/services/chatActivity";
import { useScopeChoices } from "@/composables/useScopeChoices";
import PersistentAnnouncements from "@/components/announcements/PersistentAnnouncements.vue";

import {
    getCustomerIDInStorage,
    getTokenInStorage,
    removeTokenInStorage
} from "@/classes/MobileStorage.js";
import { queryClient } from "@/plugins/vueQuery";
import { reconcileActiveQueries } from "@/services/nativeResumeReconciliation";
import { invalidateQueryCachesForEmitterEvent } from "@/services/queryInvalidation";

// EXPERIMENT (framed app shell): single kill switch. Set to false to restore
// the original edge-to-edge shell without touching anything else.
const FRAMED_SHELL = true;

export default {
    components: {
        ActionMenu,
        Loading,
        SideNavBar,
        SideNavContent,
        ContextMenu,
        UserSwitcher,
        ScanLabelModal,
        Legal,
        ForkModal,
        TrialExpiredAlert,
        DevPanel,
        BiometricEnrollModal,
        CommandPalette,
        MobileBottomBar,
        PersistentAnnouncements,
        ChatNotifications
    },
    provide() {
        return { globalLocationScope: computed(() => this.framedShell) };
    },
    inject: {
        onLoad: { default: null }
    },
    setup() {
        const { count } = useChatSummary();
        return {
            chatUnreadCount: count,
            chatEnabled: computed(chatAccessEnabled),
            ...useScopeChoices()
        };
    },

    data() {
        return {
            showModal: false,
            isAwaitingLogin: true,
            requiredLegalDocuments: [],
            isTrialExpired: false,
            activeTimer: null,
            loggingIn: true,
            initialLoginLoading: false, // Used to show the loading while the user is being logged in
            showRouteProgress: false,
            routeProgressTimer: null,
            userData: {},
            biometryType: null,
            pendingBiometricEnroll: null,
            appStateChangeListener: null,
            notificationPushCleanup: null,
            clearAuthHandler: null,
            pinSetHandler: null,
            loginHandler: null,
            tawkUserChangedHandler: null,
            biometricEnrollHandler: null,
            logoutTransition: null,
            db: null,
            currentSystemBarsStyle: null,
            mobileStyles: {
                // Let the page continue behind the detached controls. The
                // masked veil, not a clipped scrollport, owns the transition.
                height: "100svh",
                paddingTop:
                    "max(calc(var(--forkto-safe-area-top) - var(--forkto-mobile-header-safe-overlap)), 0rem)",
                "--forkto-page-min-height": "100%",
                width: "100%",
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehaviorX: "none"
            },
            // EXPERIMENT (framed app shell): logo for the global top bar.
            textLogoBlack
        };
    },
    computed: {
        ...mapGetters({
            domain: "domain",
            isRouteLoading: "isRouteLoading"
        }),

        searchShortcutHint() {
            const platform =
                typeof navigator === "undefined"
                    ? ""
                    : navigator.userAgentData?.platform || navigator.platform || "";
            return /mac/i.test(platform) ? "⌘K" : "Ctrl+K";
        },

        isPublicRoute() {
            return Boolean(this.$route.meta.publicLayout);
        },

        isDashboardRoute() {
            return this.$route.name === "Home";
        },

        // EXPERIMENT (framed app shell): monogram for the top-bar account button.
        // First + last initial, styled "Sa" / "Mu" rather than "SA" / "MU".
        accountInitials() {
            const first = (this.userData?.first_name || "").trim();
            const last = (this.userData?.last_name || "").trim();
            if (first && last) {
                return first.charAt(0).toUpperCase() + last.charAt(0).toLowerCase();
            }
            // One name only: take its first two letters so the monogram keeps
            // its shape instead of collapsing to a single glyph.
            const solo = first || (this.userData?.email_address || "").trim();
            if (!solo) return "";
            return solo.charAt(0).toUpperCase() + solo.charAt(1).toLowerCase();
        },

        sidebarCollapsed() {
            return this.$store.state.sidebarCollapsed;
        },

        // Help article for the current page (route meta.helpUrl, inherited
        // from the nearest ancestor route), falling back to the help centre.
        contextHelp() {
            return resolveHelpForRoute(this.$route);
        },

        // Same gate as the scan action on IntakesView.
        canScanLabel() {
            return [
                this.Flags.MeatModule,
                this.Flags.IngredientsModule,
                this.Flags.PackagingModule
            ].some((flag) => this.hasFeatureFlag(flag));
        },

        accountName() {
            const name = [this.userData?.first_name, this.userData?.last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
            return name || this.userData?.email_address || "";
        },

        accountEmail() {
            return this.userData?.email_address || "";
        },

        // The easy-user selection changes the active working identity, not the
        // authenticated session. Keep this tied to the JWT role so an admin can
        // always tell which privileges they signed in with.
        accountRoleLabel() {
            if (this.sessionIsAdminOverride) return "Admin override";
            const role = this.$store.getters.realUserRole?.();
            if (!role) return "";
            return role.charAt(0).toUpperCase() + role.slice(1);
        },

        // EXPERIMENT (framed app shell): content sits in an inset rounded panel
        // floating on the sunk app background, instead of running edge-to-edge.
        // Flip FRAMED_SHELL to false to switch the whole thing off.
        framedShell() {
            return FRAMED_SHELL && !this.isPublicRoute && !this.isAwaitingLogin && !this.isMobile;
        },

        sidebarColumnStyle() {
            return {
                width: this.$store.state.sidebarCollapsed ? "3.5em" : "14em",
                height: this.framedShell ? "100%" : "100svh",
                overflowY: "auto",
                transition: "width 0.18s ease-in-out"
            };
        },

        userDataLoaded() {
            if (this.userIsAdminOverride) {
                return true;
            }

            return Object.keys(this.userData).length > 0;
        },

        userIsAdminOverride() {
            return this.$store.getters.userID() == import.meta.env.VITE_ADMIN_ID;
        },

        // True when the underlying session token is the admin-override token, even
        // if EasyUserSwitch has a staff member selected (which makes userID() —
        // and thus userIsAdminOverride — return that staff id instead). Used to
        // gate the legal modal: an override session writes legal acceptance against
        // the JWT id "ADMIN OVERRIDE", which the backend rejects, so the modal must
        // stay hidden for it regardless of who is selected.
        sessionIsAdminOverride() {
            return this.$store.getters.tokenUserID() == import.meta.env.VITE_ADMIN_ID;
        },

        isMobile() {
            return this.$store.state.isMobile;
        },

        canAccessDevMode() {
            return this.$store.getters.canAccessDevMode();
        }
    },
    watch: {
        async chatEnabled(enabled) {
            if (enabled) return;
            await queryClient.cancelQueries({ queryKey: ["chat"] });
            queryClient.removeQueries({ queryKey: ["chat"] });
            if (featureFlagsLoaded.value && this.$route.name === "Chat")
                void this.$router.replace({
                    name: "PageUnavailable",
                    query: { page: "Team chat", reason: "feature" }
                });
        },
        loggingIn: "syncSystemBarsStyle",
        isAwaitingLogin: "syncSystemBarsStyle",
        initialLoginLoading: "syncSystemBarsStyle",
        isRouteLoading(loading) {
            window.clearTimeout(this.routeProgressTimer);
            this.routeProgressTimer = null;

            if (!loading) {
                this.showRouteProgress = false;
                return;
            }

            // Fast and preloaded navigations should feel instant. Only reveal
            // progress when a lazy route takes long enough to be noticeable.
            this.routeProgressTimer = window.setTimeout(() => {
                if (this.isRouteLoading) this.showRouteProgress = true;
            }, 140);
        },
        "$store.state.sidebarOpen": "syncSystemBarsStyle",
        "$store.state.commandPaletteOpen": "syncSystemBarsStyle"
    },
    async created() {
        this.initialLoginLoading = true;
        this.syncSystemBarsStyle();

        try {
            this.setUpHelpers();
            this.setUpEventListeners();

            // Step 1: Get customer ID (tenant)
            const customerID = normalizeCustomerID(await this.getCustomerIDFromStorage());
            if (!customerID) {
                // No tenant - user needs to login
                this.setIsLoggingIn(false);
                return;
            }
            this.$store.commit("setCustomerID", customerID);
            setAPICreds(customerID);

            // Step 1.5: Biometric-first login (mobile only)
            if (this.$store.state.isMobileOS) {
                const biometricStatus = await this.attemptBiometricLogin();
                if (biometricStatus === "unlocked") {
                    await this.postLoginActions(null);
                    await this.checkAndHandleTrialExpired();
                    return;
                }
                if (biometricStatus === "locked") {
                    this.setIsLoggingIn(false);
                    return;
                }
            }

            // Step 2: Get stored token
            let token = await getTokenInStorage();
            if (token === "undefined" || token === "null") {
                await removeTokenInStorage();
                token = null;
            }

            if (!token) {
                // No token - user needs to login
                this.setIsLoggingIn(false);
                return;
            }

            // Step 3: Check if token is expired and refresh if needed
            this.$store.commit("setToken", token);

            if (this.$store.getters.isTokenExpired()) {
                // Token expired - try to refresh
                try {
                    token = await API.Auth.refreshAccessToken();
                    // refreshAccessToken updates store, axios header, and storage
                } catch (refreshError) {
                    console.log("Token refresh failed:", refreshError.message);
                    // clearAuth is already called by refreshAccessToken on failure
                    this.setIsLoggingIn(false);
                    return;
                }
            }

            // Step 4: Validate token domain matches current tenant
            if (!this.$store.getters.isTokenValid()) {
                // Token domain mismatch or other validation issue
                await API.Auth.clearAuth();
                this.setIsLoggingIn(false);
                return;
            }

            // Step 5: Set up user session
            await API.Auth.setUserData(this.$store.getters.userID(), token);

            // Step 6: Complete login
            await this.postLoginActions(null);
            await this.checkAndHandleTrialExpired();
        } catch (error) {
            console.error("Authentication error:", error);
            await API.Auth.clearAuth();
            this.setIsLoggingIn(false);
        } finally {
            markAppReady();
            this.initialLoginLoading = false;
        }
    },
    mounted() {
        this.syncSystemBarsStyle();
    },

    unmounted() {
        this.clearActiveTimer();
        window.clearTimeout(this.routeProgressTimer);
        if (this.pinSetHandler) this.emitter.off("pin-set", this.pinSetHandler);
        this.emitter.off("logout", this.handleLogout);
        if (this.loginHandler) this.emitter.off("login", this.loginHandler);
        if (this.tawkUserChangedHandler) {
            this.emitter.off("tawk-user-changed", this.tawkUserChangedHandler);
        }
        if (this.biometricEnrollHandler) {
            this.emitter.off("biometric-enroll", this.biometricEnrollHandler);
        }
        if (this.clearAuthHandler) this.emitter.off("clear-auth", this.clearAuthHandler);
        if (this.notificationPushCleanup)
            Promise.resolve(this.notificationPushCleanup).then((cleanup) => cleanup?.());
        void stopNotificationDevices();
        if (this.appStateChangeListener) {
            Promise.resolve(this.appStateChangeListener).then((listener) => listener?.remove?.());
            this.appStateChangeListener = null;
        }

        // Clean up wildcard query cache handler
        if (this.queryCacheHandler) {
            this.emitter.off("*", this.queryCacheHandler);
        }
    },
    methods: {
        openCommandPalette(event) {
            this.emitter.emit("command-palette:open", { trigger: event?.currentTarget });
        },
        openScanLabel() {
            this.emitter.emit(SCAN_LABEL_OPEN);
        },

        toggleSidebarCollapsed() {
            this.$store.commit("setSidebarCollapsed", !this.$store.state.sidebarCollapsed);
        },

        // Mirrors SideNavContent.openSupport: the sidebar's Support entry opens
        // the Tawk widget rather than navigating anywhere.
        openSupport() {
            openSupport();
        },
        async syncSystemBarsStyle() {
            if (!this.$store.state.isMobileOS || !Capacitor.isNativePlatform()) {
                return;
            }

            // Dark style = light icons for dark authentication/search surfaces.
            // The navigation drawer now shares the light application chrome.
            const wantsDark =
                this.loggingIn ||
                this.isAwaitingLogin ||
                this.initialLoginLoading ||
                this.$store.state.commandPaletteOpen;

            const nextStyle = wantsDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light;

            if (this.currentSystemBarsStyle === nextStyle) {
                return;
            }

            this.currentSystemBarsStyle = nextStyle;

            try {
                await SystemBars.setStyle({ style: nextStyle });
            } catch (error) {
                this.currentSystemBarsStyle = null;
                console.warn("Unable to update system bars style:", error);
            }
        },
        async getCustomerIDFromStorage() {
            // Set the CUSTOMER ID from the URL if it exists
            if (!this.$store.state.isMobileOS) {
                // Dev-only tenant impersonation (DevPanel, localhost/staging): when
                // a tenant override is set, it takes precedence over the subdomain
                // so local code runs against a tenant loaded via load-tenant.sh.
                const tenantOverride = this.$store.state.devOverrides.tenantOverride;
                if (tenantOverride) {
                    return tenantOverride;
                }

                let customerIDFromStorage = null;
                const url = new URL(window.location.href);
                if (url.hostname == "localhost" || url.hostname == "192.168.1.4") {
                    customerIDFromStorage = "staging";
                } else {
                    customerIDFromStorage = url.hostname.split(".")[0];
                }

                return customerIDFromStorage;
            }

            return await getCustomerIDInStorage();
        },
        setUpHelpers() {
            new Tooltip(document.body, {
                selector: "[data-bs-toggle='tooltip']"
            });
        },
        setUpEventListeners() {
            this.clearAuthHandler = async () => {
                this.$toast.error("Auth cleared");
            };
            this.emitter.on("clear-auth", this.clearAuthHandler);

            // Centralized Vue Query cache invalidation via wildcard listener.
            // Catches both direct API mutations (e.g., "product-add") and
            // WebSocket events (e.g., "live-update:product") from other browsers.
            this.queryCacheHandler = (type) => {
                invalidateQueryCachesForEmitterEvent(type, queryClient);
            };
            this.emitter.on("*", this.queryCacheHandler);

            // Refresh token when app resumes from background (mobile)
            if (this.$store.state.isMobileOS) {
                this.notificationDevicesReady = startNotificationDevices({
                    api: API.User,
                    identity: () => ({
                        user: this.$store.getters.tokenUserID(),
                        tenant: this.$store.state.customerID,
                        token: this.$store.state.token
                    }),
                    navigate: (target) => this.$router.push(target).catch(() => {}),
                    invalidate: () =>
                        queryClient.invalidateQueries({ queryKey: ["notification-devices"] })
                }).catch(() => {});
                this.notificationPushCleanup = installNotificationPush({
                    plugin: PushNotifications,
                    session: () => ({
                        chatEnabled: this.chatEnabled,
                        ready:
                            featureFlagsLoaded.value &&
                            !this.isAwaitingLogin &&
                            !this.initialLoginLoading &&
                            !this.$store.state.authUnlockRequired &&
                            this.$store.getters.isTokenValid(),
                        tenant: this.$store.state.customerID,
                        user: this.$store.getters.tokenUserID(),
                        role: this.$store.getters.realUserRole()
                    }),
                    navigate: (target) => this.$router.push(target).catch(() => {}),
                    invalidate: (key) => {
                        return queryClient.invalidateQueries({ queryKey: key });
                    },
                    watchSession: (flush) =>
                        this.$watch(
                            () => [
                                this.isAwaitingLogin,
                                this.initialLoginLoading,
                                this.$store.state.token,
                                this.$store.state.customerID,
                                this.$store.state.authUnlockRequired,
                                featureFlagsLoaded.value,
                                this.chatEnabled
                            ],
                            flush
                        )
                }).catch(() => null);
                CapacitorApp.getState()
                    .then(({ isActive }) => {
                        chatAppActive.value = isActive;
                        if (!isActive) API.LiveUpdate.Close();
                    })
                    .catch(() => {});
                this.appStateChangeListener = CapacitorApp.addListener(
                    "appStateChange",
                    async ({ isActive }) => {
                        chatAppActive.value = isActive;
                        if (!isActive) {
                            API.LiveUpdate.Close();
                            return;
                        }
                        if (await checkForAppUpdate({ force: true })) return;
                        if (this.$store.state.authUnlockRequired) return;
                        const token = this.$store.state.token;
                        if (!token) return;
                        if (this.$store.getters.isTokenExpired()) {
                            try {
                                await API.Auth.refreshAccessToken();
                            } catch (error) {
                                console.log("Background refresh failed:", error.message);
                                this.emitter.emit("logout");
                                return;
                            }
                        }
                        if (!userIsRole("auditor")) {
                            void this.$store.dispatch("subscribeToLiveUpdates");
                        }
                        await reconcileActiveQueries(queryClient);
                        await this.notificationDevicesReady;
                        void syncNotificationDevice().catch(() => {});
                    }
                );
            }

            this.biometricEnrollHandler = ({ refreshToken, userID, biometryType }) => {
                this.biometryType = biometryType;
                this.pendingBiometricEnroll = { refreshToken, userID };
                this.$nextTick(() => {
                    this.$refs["biometric-enroll-modal"]?.open();
                });
            };
            this.emitter.on("biometric-enroll", this.biometricEnrollHandler);

            this.tawkUserChangedHandler = async () => {
                await this.logoutTawk();
                await this.loginTawk();
            };
            this.emitter.on("tawk-user-changed", this.tawkUserChangedHandler);

            this.emitter.on("logout", this.handleLogout);
            this.pinSetHandler = async () => {
                this.$toast.success("PIN saved");
                if (this.userIsAdminOverride) return;
                const sessionRevision = this.$store.state.authSessionRevision;
                try {
                    const response = await API.User.GetMe();
                    if (
                        !this.$store.state.token ||
                        this.$store.state.authSessionRevision !== sessionRevision
                    )
                        return;
                    this.userData = response;
                    this.$store.commit("setUser", response);
                    if (this.hasFeatureFlag(this.Flags.KioskMode) && response.pin)
                        this.setKioskUsers();
                } catch (error) {
                    // Saving succeeded. Normal login reconciliation can refresh identity later.
                    console.error("PIN identity refresh failed:", error);
                }
            };
            this.emitter.on("pin-set", this.pinSetHandler);
            this.loginHandler = async (payload) => {
                // This handler runs from a fire-and-forget `emitter.emit("login")`, so a
                // throw here is otherwise swallowed silently (the user would see a success
                // toast but never leave the login screen). Surface failures instead.
                try {
                    const login = this.normalizeLoginPayload(payload);
                    await this.postLoginActions(login.route);
                    await this.maybeOfferBiometricEnrollment(login);
                    this.checkAndHandleTrialExpired();
                    try {
                        this.$gtag.event("login", {
                            user_id: this.$store.getters.userID(),
                            domain: this.$store.getters.domain
                        });
                    } catch (error) {
                        //
                    }
                } catch (error) {
                    console.error("Post-login handling failed:", error);
                    this.$toast?.error?.("Something went wrong after sign-in. Please try again.");
                }
            };
            this.emitter.on("login", this.loginHandler);
        },
        async checkAndHandleTrialExpired() {
            if (userIsRole("supplier")) {
                return;
            }

            const sessionRevision = this.$store.state.authSessionRevision;
            try {
                const clientResponse = this.$store.state.client
                    ? this.$store.state.client
                    : await API.Client.Get();
                if (
                    !this.$store.state.token ||
                    this.$store.state.authSessionRevision !== sessionRevision
                ) {
                    return;
                }
                // Setting the client data in the store.
                // Required to mitigate race conditions expecially for settings
                this.$store.commit("setClient", clientResponse);
                // Granted access always wins over a stale trial date. This is
                // especially important for manually administered arrangements
                // such as Free Until Five, which are not expiring trials.
                this.isTrialExpired = false;
                if (
                    !clientResponse.has_access &&
                    clientResponse.trial &&
                    clientResponse.trial_expires
                ) {
                    const trialExpires =
                        clientResponse.trial_expires - new Date().setHours(0, 0, 0, 0);
                    if (trialExpires <= 0) {
                        this.isTrialExpired = true;
                    }
                }
            } catch (error) {
                console.error(error);
            }
        },
        normalizeLoginPayload(payload) {
            const isLoginPayload =
                payload &&
                typeof payload === "object" &&
                ("route" in payload ||
                    "loginMethod" in payload ||
                    "refreshToken" in payload ||
                    "userID" in payload);

            if (isLoginPayload) {
                return {
                    route: payload.route || null,
                    loginMethod: payload.loginMethod || "unknown",
                    refreshToken: payload.refreshToken || null,
                    userID: payload.userID || null
                };
            }

            return {
                route: payload || null,
                loginMethod: "unknown",
                refreshToken: null,
                userID: null
            };
        },
        async maybeOfferBiometricEnrollment({ refreshToken, userID, loginMethod }) {
            if (!this.$store.state.isMobileOS) return;

            try {
                const { BiometricService } = await import("@/services/BiometricService.js");
                const kioskUsers = getKioskUsers(this.$cookies, this.$store.state.customerID);
                const availability = await BiometricService.shouldOfferEnrollment({
                    refreshToken,
                    userID,
                    loginMethod,
                    isKioskMode: this.hasFeatureFlag(this.Flags.KioskMode),
                    hasKioskUsers: kioskUsers.length > 0
                });

                if (!availability) return;

                setTimeout(() => {
                    this.biometryType = availability.biometryType;
                    this.pendingBiometricEnroll = {
                        refreshToken,
                        userID: String(userID)
                    };
                    this.$nextTick(() => {
                        this.$refs["biometric-enroll-modal"]?.open();
                    });
                }, 1200);
            } catch (error) {
                console.log("Biometric enrollment check failed:", error.message);
            }
        },
        async attemptBiometricLogin() {
            let BiometricService = null;
            try {
                ({ BiometricService } = await import("@/services/BiometricService.js"));

                const isEnabled = await BiometricService.isEnabled();
                if (!isEnabled) return "skipped";

                const { isAvailable } = await BiometricService.isAvailable();
                if (!isAvailable) {
                    this.$store.commit("setAuthUnlockRequired", true);
                    return "locked";
                }

                await API.Auth.unlockWithBiometrics();
                return "unlocked";
            } catch (error) {
                if (
                    BiometricService &&
                    (await BiometricService.clearStaleEnrollmentIfTokenMissing(error))
                ) {
                    this.$store.commit("setAuthUnlockRequired", false);
                    return "skipped";
                }

                this.$store.commit("setAuthUnlockRequired", true);
                console.log("Biometric login required:", error.message);
                return "locked";
            }
        },
        async handleBiometricEnrolled() {
            try {
                const { BiometricService } = await import("@/services/BiometricService.js");
                await BiometricService.enroll(
                    this.pendingBiometricEnroll.refreshToken,
                    this.pendingBiometricEnroll.userID
                );
                this.$toast.success("Biometric login enabled!");
            } catch (error) {
                this.$toast.error("Could not enable biometric login");
            }
            this.pendingBiometricEnroll = null;
        },
        async handleBiometricDismissed() {
            try {
                const { BiometricService } = await import("@/services/BiometricService.js");
                await BiometricService.dismissPrompt(this.pendingBiometricEnroll?.userID);
            } catch {
                // Non-critical.
            }
            this.pendingBiometricEnroll = null;
        },
        clearActiveTimer() {
            if (!this.activeTimer) return;
            clearInterval(this.activeTimer);
            this.activeTimer = null;
        },
        async updateActiveStatusForVisibility() {
            if (typeof document === "undefined") return;
            if (typeof navigator !== "undefined" && navigator.onLine === false) return;

            if (document.visibilityState === "hidden") {
                await API.User.SetActiveStatus(false);
            } else if (document.visibilityState === "visible") {
                await API.User.SetActiveStatus(true);
            }
        },
        startActiveStatusTimer() {
            this.clearActiveTimer();
            void this.updateActiveStatusForVisibility().catch(() => {});
            this.activeTimer = setInterval(() => {
                void this.updateActiveStatusForVisibility().catch(() => {});
            }, 60000);
        },
        async requestLogout() {
            try {
                await API.logoutUser();
            } finally {
                // AuthService normally emits `logout` after clearing the local
                // credentials. The fallback keeps an explicit sign-out reliable
                // even if a platform plugin or server-side cleanup fails first.
                await this.handleLogout();
            }
        },
        async handleLogout() {
            if (this.logoutTransition) return this.logoutTransition;

            this.logoutTransition = (async () => {
                this.clearActiveTimer();
                if (!this.hasFeatureFlag(this.Flags.KioskMode)) {
                    removeKioskUsers(this.$cookies);
                }

                // Hide every authenticated surface before doing asynchronous
                // storage cleanup. This prevents tenant data and the sidebar from
                // flashing while native secure storage is being cleared.
                this.isAwaitingLogin = true;
                this.setIsLoggingIn(false);
                this.userData = {};
                this.requiredLegalDocuments = [];
                this.pendingBiometricEnroll = null;
                this.isTrialExpired = false;
                this.$store.commit("setSidebarOpen", false);
                this.$store.commit("setCommandPaletteOpen", false);
                this.$store.commit("setUser", null);

                featureFlags.clearFeatureFlags();
                queryClient.clear();
                void this.logoutTawk();

                await API.clearAuth();
                await this.$router.replace({ name: "LoginPage" }).catch(() => {});
            })();

            try {
                await this.logoutTransition;
            } finally {
                this.logoutTransition = null;
            }
        },
        setKioskUsers() {
            const customerID = this.$store.state.customerID;
            const kioskUsers = getKioskUsers(this.$cookies, customerID);

            if (!kioskUsers.some((user) => user.id === this.userData.id)) {
                kioskUsers.push({
                    id: this.userData.id,
                    first_name: this.userData.first_name,
                    last_name: this.userData.last_name
                });
            }

            setKioskUsers(this.$cookies, customerID, kioskUsers);
        },
        async postLoginActions(route) {
            this.setIsLoggingIn(false);
            const sessionRevision = this.$store.state.authSessionRevision;

            // Resolve the user for display. Every login path (password, quick-login,
            // biometric, cold boot) runs setUserData() first, which fetches and commits
            // the user, so this normally just mirrors store state. On the rare path where
            // it hasn't, fetch in the background — a failure here must NOT strand the user
            // on the login form, because navigation below no longer waits on it.
            if (this.$store.state.user) {
                this.userData = this.$store.state.user;
            } else if (!this.userIsAdminOverride) {
                API.User.GetMe()
                    .then((user) => {
                        if (
                            !this.$store.state.token ||
                            this.$store.state.authSessionRevision !== sessionRevision
                        ) {
                            return;
                        }
                        this.userData = user;
                        this.$store.commit("setUser", user);
                    })
                    .catch((error) => console.error("Post-login GetMe failed:", error));
            }

            // Hydrate in the BACKGROUND — never gate navigation on it. Previously the
            // push below sat after `await Promise.all([loadAllData, loadFeatureFlags])`,
            // so a single stalled request (there was no axios timeout) left a "logged in"
            // user stuck on the login form. Feature flags are kicked off before the push
            // so the router guard's `await flagsReady` can resolve for a flag-gated
            // redirect target; loadAllData fills the Vuex lists while the landing screen
            // renders empty-then-populated (the location/department watchers re-resolve
            // the active scope once the lists arrive).
            if (this.$store.getters.isTokenValid()) {
                const flagsLoaded = featureFlags.loadFeatureFlags().catch((error) => {
                    console.error("Feature flag load failed:", error);
                });
                // Suppliers don't need the full list payload — their UI surface is
                // limited to complaints + profile.
                if (!userIsRole("supplier")) {
                    flagsLoaded
                        .then(() => {
                            if (
                                this.$store.state.authSessionRevision !== sessionRevision ||
                                !this.$store.getters.isTokenValid()
                            )
                                return;
                            return this.$store.dispatch("loadAllData");
                        })
                        .catch((error) => {
                            console.error("Background data load failed:", error);
                        });
                }
                // Kiosk bookkeeping and legal acceptance depends on feature flags being loaded
                // (KioskMode gates whether the kiosk_users cookie is kept), so run it once
                // flags resolve — after navigation, never blocking it.
                flagsLoaded.then(() => this.applyPostLoginGatedActions());
            }

            // Navigate immediately. This is the core fix — see the comment above.
            if (route) {
                await this.$router.push(route).catch(() => {});
            }
            this.isAwaitingLogin = false;
            this.initialLoginLoading = false;

            this.loginTawk();

            // Used to monitor user active status
            this.startActiveStatusTimer();

            // Subscribe to live updates
            if (!userIsRole("auditor")) {
                this.$store.dispatch("subscribeToLiveUpdates");
            }

            if (this.$store.state.isMobileOS) {
                await this.notificationDevicesReady;
                void syncNotificationDevice({ requestPermission: true }).catch(() => {});
            }
        },
        // Kiosk-user bookkeeping and legal acceptance. Split out of
        // postLoginActions because these depend on feature flags being loaded
        // (KioskMode), so they run in a post-flags continuation rather than blocking
        // navigation. Guarded on userData, which is set synchronously on every real
        // login path before this runs.
        applyPostLoginGatedActions() {
            // Perform one-time actions after login
            if (this.hasFeatureFlag(this.Flags.KioskMode)) {
                if (this.userData?.pin?.length > 0) {
                    // Get kiosk users from localStorage
                    this.setKioskUsers();
                }
            } else {
                removeKioskUsers(this.$cookies);
            }

            // Check if the user has accepted the legal terms
            if (this.userData) {
                // Parse legal document last updated dates from environment variables
                const eulaLastUpdated = new Date(
                    import.meta.env.VITE_EULA_LAST_UPDATED || "2000-01-01"
                ).getTime();
                const tosLastUpdated = new Date(
                    import.meta.env.VITE_TOS_LAST_UPDATED || "2000-01-01"
                ).getTime();
                const privacyLastUpdated = new Date(
                    import.meta.env.VITE_PRIVACY_LAST_UPDATED || "2000-01-01"
                ).getTime();

                // Determine which documents need re-acceptance
                const required = [];
                if (
                    !this.userData.legal?.eula_accepted ||
                    this.userData.legal.eula_accepted < eulaLastUpdated
                ) {
                    required.push("eula");
                }
                if (
                    !this.userData.legal?.tos_accepted ||
                    this.userData.legal.tos_accepted < tosLastUpdated
                ) {
                    required.push("tos");
                }
                if (
                    !this.userData.legal?.privacy_policy_accepted ||
                    this.userData.legal.privacy_policy_accepted < privacyLastUpdated
                ) {
                    required.push("privacy");
                }

                if (required.length > 0 && !this.sessionIsAdminOverride) {
                    this.requiredLegalDocuments = required;
                    setTimeout(() => {
                        try {
                            this.$refs["legal-modal"].open();
                        } catch (error) {
                            //
                        }
                    }, 1000);
                }
            }
        },
        setIsLoggingIn(value) {
            this.loggingIn = value;
            setLoggingIn(value);
        },
        closeLegalModal() {
            this.$refs["legal-modal"].close();
        },
        hasTawkIntegration() {
            // window.Tawk_API is always present (main.js installs a no-op stub),
            // so gate on the real widget being enabled — otherwise loginTawk
            // hits /v2/tawk/auth, which 503s in local dev (secure mode unset).
            return (
                thirdPartyScriptsEnabled &&
                typeof window !== "undefined" &&
                (typeof window.Tawk_API === "object" || typeof this.onLoad === "function")
            );
        },
        getTawkTargetUserID() {
            const easyUserID = localStorage.getItem("easy_user_id");
            if (easyUserID && easyUserID !== import.meta.env.VITE_ADMIN_ID) {
                return easyUserID;
            }
            return null;
        },
        buildTawkUser(userID) {
            const user =
                this.$store.state.userList.find((u) => u.id === userID) ||
                this.$store.state.user ||
                this.userData ||
                {};
            const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
            const tawkUser = {
                userId: userID,
                name: fullName || user.email_address || user.username || userID
            };

            if (user.email_address) {
                tawkUser.email = user.email_address;
            }

            if (user.phone_number) {
                tawkUser.phone = user.phone_number;
            }

            return tawkUser;
        },
        async loginTawk() {
            if (!this.hasTawkIntegration()) return;
            const targetUserID = this.getTawkTargetUserID();
            const identityKey = targetUserID || this.$store.getters.userID();

            return tawkSession
                .ensureLoggedIn({
                    identityKey,
                    onLoad: this.onLoad,
                    loadPayload: async () => {
                        const auth = await API.Tawk.GetAuth(targetUserID);
                        if (!auth?.userId || !auth?.hash) return null;

                        // Support context shown to agents in chat. Tenant is
                        // also a tag for dashboard filtering; role remains an
                        // attribute so role tags do not accumulate on kiosks.
                        const tenant = this.$store.getters.customerID();
                        const role = this.$store.getters.userRole();
                        return {
                            ...this.buildTawkUser(auth.userId),
                            userId: auth.userId,
                            hash: auth.hash,
                            ...(tenant && { tenant }),
                            ...(role && { role })
                        };
                    },
                    afterLogin: (tawkAPI) => {
                        const tenant = this.$store.getters.customerID();
                        if (!tenant || typeof tawkAPI.addTags !== "function") return;
                        tawkAPI.addTags([tenant], (error) => {
                            if (error) console.warn("Tawk addTags", error);
                        });
                    }
                })
                .catch((error) => {
                    console.error("Tawk auth", error);
                    return false;
                });
        },
        async logoutTawk() {
            await tawkSession.logout();
        }
    }
};
</script>

<style>
:root {
    --forkto-safe-area-top: var(--safe-area-inset-top, env(safe-area-inset-top, 0px));
    --forkto-safe-area-right: var(--safe-area-inset-right, env(safe-area-inset-right, 0px));
    --forkto-safe-area-bottom: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));
    --forkto-safe-area-left: var(--safe-area-inset-left, env(safe-area-inset-left, 0px));
    --forkto-mobile-header-safe-overlap: 0.5rem;
    --forkto-mobile-nav-control-size: 3rem;
    --forkto-mobile-nav-edge-gap: 1rem;
    --forkto-mobile-nav-optical-bottom: max(
        var(--forkto-mobile-nav-edge-gap),
        calc(var(--forkto-safe-area-bottom) - 1.25rem)
    );
    --forkto-mobile-nav-clearance: calc(
        var(--forkto-mobile-nav-control-size) + var(--forkto-mobile-nav-optical-bottom) + 0.5rem
    );
}

.fixed-bottom {
    padding-bottom: max(var(--forkto-safe-area-bottom), 1rem) !important;
}

/* Desktop owns scrolling at the application shell. On mobile, the main region
   owns scrolling so the fixed bottom bar stays outside the scrollport. Hide the
   narrow-layout scrollbar track because touch scrolling remains available and
   a classic desktop track should not cut through the mobile page chrome. */
.app-main-scroll {
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    background: var(--forkto-surface, #ffffff);
}

/* The inner wrapper previously carried an inline `position: relative`. It now
   carries a class so the framed shell can turn it into the content panel. */
.app-main-inner {
    position: relative;
}

/* ---------------------------------------------------------------------------
   EXPERIMENT (framed app shell)

   Klaviyo-style framing in Forkto's own palette: the app background becomes the
   sunk tone, and page content lives in an inset rounded panel that owns its own
   scrollport. The nav stays flush to the window edge.

   Everything below is scoped to `.app-framed`, which `App.vue` only applies when
   the FRAMED_SHELL flag is on (desktop, private routes). Remove the flag or the
   class and the original edge-to-edge shell returns untouched.
--------------------------------------------------------------------------- */
.app-framed {
    /* `.forkto-page-shell` sizes against this token. Inside the panel the page
       should fill the panel, not the viewport. */
    --forkto-page-min-height: 100%;
    --forkto-page-gutter-desktop: 2rem;

    height: calc(100svh - var(--app-topbar-height));
    background: var(--forkto-app-ground);
}

/* The sidebar and scroll region hardcode 100svh for the unframed shell; inside
   the frame they fill the space left under the top bar instead. */
.app-framed .app-main-scroll {
    height: 100% !important;
}

/* The top bar owns the wordmark, so the sidebar's own logo would duplicate it.
   The collapse toggle beside it stays. */
.app-framed .sidebar-logo-link {
    display: none;
}

/* The scrollport moves inward: this element now only frames and pads. */
.app-framed .app-main-scroll {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    overflow: hidden;
    /* This element is the 8px gutter around the panel. The unframed shell
       paints it white as the canvas base; framed, it must let the ground
       show through or the panel's corners land on white and the frame
       reads as a box. */
    background: transparent;
}

/* The panel itself. `overflow: hidden` keeps a sticky PageHeader from bleeding
   past the rounded corners on views that still use one. */
/* Inside the frame the panel is the only ground. Migrated pages paint a sunk
   canvas for the unframed shell; here that would nest a second floor inside
   the surface, so it is flattened and cards sit bordered on white. */
.app-framed .forkto-page-canvas,
.app-framed .forkto-page-shell {
    background: transparent;
}

.app-framed .app-main-inner {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    background: var(--forkto-surface);
    border: 1px solid var(--forkto-line);
    border-radius: 16px;
}

.app-framed #main-content {
    flex: 1 1 auto;
    min-height: 0;
    border-radius: inherit;
    overflow-y: auto;
    overflow-x: hidden;
    /* Keep vertical scroll chaining inside the framed panel, but allow a
       horizontal trackpad gesture to reach the browser's history navigation. */
    overscroll-behavior-y: contain;
    overscroll-behavior-x: auto;
    /* A full-width scrollbar flush against the panel's right edge made the
       8px gutter beside it read as scrollbar chrome, not as the frame. Thin
       and quiet keeps the affordance without owning the edge. */
    scrollbar-width: thin;
    scrollbar-color: var(--forkto-line-strong) transparent;
}

/* Chat fills the app-owned scrollport. Headers and announcements keep their
   natural height; the conversation receives the remaining space. */
#main-content.app-main--chat {
    display: flex;
    flex-direction: column;
}

#main-content.app-main--chat > :not(.chat-page) {
    flex-shrink: 0;
}

.app-framed #main-content::-webkit-scrollbar {
    width: 8px;
}

.app-framed #main-content::-webkit-scrollbar-thumb {
    background: var(--forkto-line-strong);
    border: 2px solid transparent;
    border-radius: 999px;
    background-clip: padding-box;
}

.app-framed #main-content::-webkit-scrollbar-track {
    background: transparent;
}

/* The canvas no longer paints its own ground; the panel is the surface. */
.app-framed .forkto-page-canvas {
    background: transparent;
}

/* Global top bar --------------------------------------------------------- */

/* Defined on :root so both the bar itself and the shell's height calc read the
   same value; they previously each hardcoded 40px and could drift. */
:root {
    /* Keep the 40px control row below the status bar when the framed shell is
       displayed in an edge-to-edge iPad WebView or installed web app. The
       same height also sizes the sidebar and content panel below it. */
    --app-topbar-height: calc(40px + var(--forkto-safe-area-top));

    /* --forkto-app-ground is defined with the rest of the palette in
       custom.scss; the shell and top bar both read it from :root. */
}

.app-topbar {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: var(--app-topbar-height);
    padding: calc(0.5rem + var(--forkto-safe-area-top)) max(1.25rem, var(--forkto-safe-area-right))
        0 max(1.25rem, var(--forkto-safe-area-left));
    background: var(--forkto-app-ground, var(--forkto-surface));
}

/* Equal `flex: 1 1 0` on both flanks makes them the same width, so the search
   pill sits on the true centre line. A fixed width here plus a content-sized
   context cluster pulled it ~11px off-centre. */
.app-topbar__lead {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1 1 0;
    min-width: 0;
}

.app-topbar__logo {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
}

/* The column becomes the flex context so the nav scrolls while the collapse
   control stays pinned to the bottom instead of scrolling away with it. */
.app-framed #sidebarColumn {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden !important;
}

.app-framed #sidebarColumn > .side-nav-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
}

.app-framed #sidebarColumn > .side-nav-content::-webkit-scrollbar {
    display: none;
}

.sidebar-foot {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    margin: 0.25rem 0.5rem 0.5rem;
}

.sidebar-collapse-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
    min-height: 2.25rem;
    margin: 0;
    padding: 0.375rem 0.5rem;
    color: var(--forkto-ink-3);
    font-size: 0.75rem;
    background: none;
    border: 0;
    border-radius: var(--forkto-radius-compact);
}

.sidebar-collapse-toggle > i {
    flex: 0 0 1rem;
    width: 1rem;
    text-align: center;
    font-size: 0.6875rem;
}

.sidebar-collapse-toggle:hover {
    color: var(--forkto-ink-1);
    background: var(--forkto-surface);
}

.sidebar-collapse-toggle:focus-visible {
    outline: 2px solid var(--forkto-focus-ring);
    outline-offset: -2px;
}

.app-topbar__logo img {
    height: 1.35rem;
}

.app-topbar__command-cluster {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
}

/* Centred regardless of how wide the flanking items are. Sized to its
   content, not stretched: it is a button that opens the palette, and an
   input-shaped pill invited typing into something that is not an input. */
.app-topbar__command {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
    min-height: 2rem;
    padding: 0 0.85rem 0 0.75rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    font-weight: 500;
    white-space: nowrap;
    background: var(--forkto-surface);
    border: 1px solid var(--forkto-line-strong);
    border-radius: var(--forkto-radius-control);
    cursor: pointer;
}

.app-topbar__command:hover {
    color: var(--forkto-ink-1);
    background: var(--forkto-sunk);
}

.app-topbar__command:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.app-topbar__command-label {
    flex: 0 0 auto;
}

.app-topbar__kbd {
    margin-left: 0.25rem;
    padding: 0.05rem 0.35rem;
    color: var(--forkto-ink-3);
    font-size: 0.6875rem;
    background: var(--forkto-sunk);
    border: 1px solid var(--forkto-line);
    border-radius: 4px;
}

.app-topbar__context {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 0;
    min-width: 0;
    justify-content: flex-end;
}

/* Global utilities stay quiet beside the page's operational actions. */
.app-topbar__scan {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-height: 2rem;
    padding: 0 0.8rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    font-weight: 600;
    text-decoration: none;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
}

.app-topbar__scan:hover {
    color: var(--forkto-ink-1);
    background: var(--forkto-surface);
    border-color: var(--forkto-line);
}

.app-topbar__scan:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.app-topbar__help {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    color: var(--forkto-ink-3);
    font-size: 1rem;
    text-decoration: none;
    border-radius: 50%;
}

.app-topbar__help:hover {
    color: var(--forkto-ink-1);
    background: var(--forkto-surface);
}

.app-topbar__help:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.app-topbar__account {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-height: 2rem;
    padding: 0.125rem 0.25rem;
    background: none;
    border: 0;
    border-radius: var(--forkto-radius-control);
}

.app-topbar__account:hover {
    background: var(--forkto-sunk);
}

.app-topbar__account:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.app-topbar__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    color: var(--forkto-surface);
    font-size: 0.75rem;
    font-weight: 600;
    background: var(--forkto-ink-1);
    border-radius: 50%;
}

.app-topbar__caret {
    color: var(--forkto-ink-3);
    font-size: 0.625rem;
}

/* Account menu. Bootstrap's defaults (square-ish corners, 1rem items, blue
   hover) read as stock chrome next to the rest of the app, so the menu is
   restyled onto Forkto's tokens. */
.account-menu.dropdown-menu {
    min-width: 0;
}

.account-menu__identity {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.625rem 0.625rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid var(--forkto-line);
}

.account-menu__identity-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.account-menu__name-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
}

.account-menu__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--forkto-ink-1);
    line-height: 1.3;
}

.account-menu__role {
    flex: 0 0 auto;
    padding: 0.1rem 0.35rem;
    color: var(--forkto-ink-2);
    font-size: 0.625rem;
    font-weight: 600;
    line-height: 1.3;
    background: var(--forkto-sunk);
    border: 1px solid var(--forkto-line);
    border-radius: 999px;
}

/* An address can be far longer than the menu is wide. */
.account-menu__email {
    font-size: 0.75rem;
    color: var(--forkto-ink-3);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.account-menu__item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    width: 100%;
    padding: 0.4375rem 0.625rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    text-align: left;
    text-decoration: none;
    background: none;
    border: 0;
    border-radius: 8px;
}

.account-menu__item:hover,
.account-menu__item:focus-visible {
    color: var(--forkto-ink-1);
    background: var(--forkto-sunk);
}

.account-menu__item:focus-visible {
    outline: 2px solid var(--forkto-focus-ring);
    outline-offset: -2px;
}

.account-menu__item--danger:hover,
.account-menu__item--danger:focus-visible {
    color: var(--forkto-fail);
    background: var(--forkto-fail-soft);
}

.account-menu__divider {
    margin: 0.25rem 0.3125rem;
    border: 0;
    border-top: 1px solid var(--forkto-line);
    opacity: 1;
}

/* The wordmark and the collapse toggle both live in the top bar now, so this
   row has nothing left to hold. */
.app-framed .sidebar-header {
    display: none !important;
}

/* The nav's own right border sat 8px from the content panel's left border,
   drawing two parallel lines down the page. The panel border is the one that
   belongs to the frame, so the nav drops its own. */
.app-framed .side-nav-content {
    --side-nav-active: var(--forkto-surface);
    /* Align the Dashboard row's optical centre with the page title beside it. */
    padding-top: 2.25rem !important;
    border-right: 0 !important;
    /* Sits on the shell ground; only the content panel is a raised surface. */
    background: transparent !important;
}

/* Section labels. ALL-CAPS 700 with wide tracking gave four heavy banner rows
   in a list of ~21 items; sentence case at a lighter weight lets them recede
   into grouping cues. */
.app-framed .sidebar-section-title--desktop {
    /* 8px between a label and its first row. */
    margin-bottom: 0.5rem !important;
    color: var(--forkto-ink-3);
    /* rem, so it scaled down with the 14px base; hold it at ~11px. */
    font-size: 0.7857rem;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}

/* Tighter rhythm between groups and rows, so the list stops overflowing. */
.app-framed .sidebar-section,
.app-framed .side-nav-content--desktop-light .sidebar-section--dashboard {
    /* 20px between the last row of a section and the next label, so a
       section reads as a group: the gap above a label is clearly larger
       than the gap below it. Dashboard is its own section and gets the same. */
    margin-bottom: 1.25rem !important;
}

/* Slightly larger labels and 36px rows retain a compact desktop rhythm. */
.app-framed .side-nav-item--desktop {
    min-height: 2.25rem;
    font-size: 0.9375rem;
    padding-top: 0.375rem !important;
    padding-bottom: 0.375rem !important;
}

/* Nav is navigation only now: the context switchers and the account/utility
   entries live in the top bar instead. */
.app-framed .context-bar,
.app-framed .sidebar-section--settings,
.app-framed .sidebar-section--support,
.app-framed .sidebar-section--profile,
.app-framed .sidebar-section--promo {
    display: none;
}

.route-progress {
    position: sticky;
    z-index: 1100;
    top: 0;
    width: 100%;
    height: 0;
    overflow: visible;
    pointer-events: none;
}

.route-progress__bar {
    display: block;
    width: 38%;
    height: 3px;
    background: var(--forkto-accent, #15181a);
    border-radius: 0 999px 999px 0;
    animation: forkto-route-progress 1.1s ease-in-out infinite;
    transform-origin: left center;
}

@keyframes forkto-route-progress {
    0% {
        transform: translateX(-105%) scaleX(0.55);
    }
    55% {
        transform: translateX(115%) scaleX(1);
    }
    100% {
        transform: translateX(275%) scaleX(0.65);
    }
}

@media (prefers-reduced-motion: reduce) {
    .route-progress__bar {
        width: 100%;
        animation: none;
    }
}

#sidebarColumn {
    scrollbar-width: none;
}

#sidebarColumn::-webkit-scrollbar {
    display: none;
}

@media (max-width: 1279.98px) {
    .app-main-scroll {
        overflow: hidden;
    }

    #main-content {
        scrollbar-width: none;
    }

    #main-content::-webkit-scrollbar {
        display: none;
    }
}

/* offcanvas padding */
.offcanvas-body {
    /* padding-top: max(var(--forkto-safe-area-top), 0rem) !important; */
    padding-bottom: max(var(--forkto-safe-area-bottom), 0rem) !important;
}

#main-content:focus {
    outline: none;
}

/* Keep iOS status information legible after the sticky page header moves out
   of view. The short masked blur protects the clock, Dynamic Island, signal,
   and battery without reading as a second header or covering page controls. */
.native-safe-area-scrim::before {
    content: "";
    position: fixed;
    z-index: 49;
    top: 0;
    right: 0;
    left: 0;
    height: calc(var(--forkto-safe-area-top, env(safe-area-inset-top, 0px)) + 1.5rem);
    pointer-events: none;
    background: var(--forkto-surface, #fff);
    -webkit-backdrop-filter: blur(10px) saturate(115%);
    backdrop-filter: blur(10px) saturate(115%);
    -webkit-mask-image: linear-gradient(
        to bottom,
        #000 0,
        #000 var(--forkto-safe-area-top, env(safe-area-inset-top, 0px)),
        transparent 100%
    );
    mask-image: linear-gradient(
        to bottom,
        #000 0,
        #000 var(--forkto-safe-area-top, env(safe-area-inset-top, 0px)),
        transparent 100%
    );
}

.skip-to-main {
    position: fixed;
    z-index: 30000;
    top: max(0.5rem, var(--forkto-safe-area-top));
    left: max(0.5rem, var(--forkto-safe-area-left));
    padding: 0.65rem 0.85rem;
    color: var(--forkto-ink-1, #15181a);
    background: #ffffff;
    border: 2px solid currentColor;
    border-radius: var(--forkto-radius-control, 0.5rem);
    box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.18);
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    overflow: hidden;
    white-space: nowrap;
}

.skip-to-main:focus {
    clip: auto;
    clip-path: none;
    overflow: visible;
    outline: 3px solid var(--forkto-accent, #3f6170);
    outline-offset: 2px;
}
</style>
